import type { AdminFeedbackQueueFilters, FeedbackAttachmentInput } from '@doneverse/shared'
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { getNotifier } from '../api/index.js'
import {
  adminFeedbackService,
  feedbackService,
  uploadFeedbackFile,
  type AdminFeedbackReplyRequest,
  type FeedbackReplyRequest,
  type FeedbackTriageInput,
  type SubmitFeedbackRequest,
} from '../services/index.js'

/**
 * Picked files are uploaded here, inside the mutation, rather than when the user
 * picks them — the same shape as sending a message attachment.
 *
 * Everything else follows from it: nothing is in flight while the form sits
 * open, so there is no upload to race a Send against, abandoning a draft leaves
 * no orphaned object in storage, and one `isPending` covers the whole send.
 */
async function uploadAll(files: File[] | undefined, scopeId?: string): Promise<FeedbackAttachmentInput[]> {
  if (!files?.length) return []

  const uploaded: FeedbackAttachmentInput[] = []
  for (const file of files) {
    const result = await uploadFeedbackFile(file, scopeId)
    uploaded.push({
      file_url: result.fileUrl,
      file_name: result.fileName,
      content_type: result.contentType,
      size_bytes: result.size,
    })
  }
  return uploaded
}

/** Form input: the files as picked. The upload happens in the mutation. */
export type SubmitFeedbackVars = Omit<SubmitFeedbackRequest, 'attachments'> & { files?: File[] }
export type FeedbackReplyVars = Omit<FeedbackReplyRequest, 'attachments'> & { files?: File[] }
export type AdminFeedbackReplyVars = Omit<AdminFeedbackReplyRequest, 'attachments'> & { files?: File[] }

export const feedbackKeys = {
  all: ['feedback'] as const,
  list: ['feedback', 'list'] as const,
  thread: ['feedback', 'thread'] as const,
  adminQueue: ['feedback', 'admin', 'queue'] as const,
  adminThread: ['feedback', 'admin', 'thread'] as const,
}

/** The submitter's "Previous feedback" list. */
export const feedbackListOptions = () =>
  queryOptions({
    queryKey: feedbackKeys.list,
    queryFn: feedbackService.list,
  })

export const feedbackThreadOptions = (id: string) =>
  queryOptions({
    queryKey: [...feedbackKeys.thread, id] as const,
    queryFn: () => feedbackService.getThread(id),
    enabled: !!id,
  })

export const adminFeedbackQueueOptions = (filters: AdminFeedbackQueueFilters = {}) =>
  queryOptions({
    queryKey: [...feedbackKeys.adminQueue, filters] as const,
    queryFn: () => adminFeedbackService.listQueue(filters),
  })

export const adminFeedbackThreadOptions = (id: string) =>
  queryOptions({
    queryKey: [...feedbackKeys.adminThread, id] as const,
    queryFn: () => adminFeedbackService.getThread(id),
    enabled: !!id,
  })

export function useSubmitFeedback(opts?: { onSuccess?: (feedbackId: string) => void }) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ files, ...data }: SubmitFeedbackVars) =>
      // No scope id: the item does not exist yet, so the API scopes the object
      // by uploader instead.
      feedbackService.submit({ ...data, attachments: await uploadAll(files) }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: feedbackKeys.list })
      opts?.onSuccess?.(created.id)
    },
    onError: (error) => {
      console.error('Error submitting feedback:', error)
      getNotifier().error('Failed to submit feedback. Please try again.')
    },
  })
}

/** The submitter answering a clarifying question on their own thread. */
export function useReplyToFeedback(feedbackId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ files, ...data }: FeedbackReplyVars) =>
      feedbackService.reply(feedbackId, { ...data, attachments: await uploadAll(files, feedbackId) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...feedbackKeys.thread, feedbackId] })
      queryClient.invalidateQueries({ queryKey: feedbackKeys.list })
    },
    onError: (error) => {
      console.error('Error replying to feedback:', error)
      getNotifier().error('Could not send your reply. Please try again.')
    },
  })
}

/** Categorise, assign an owner, move status, or store the ClickUp link. */
export function useTriageFeedback(feedbackId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: FeedbackTriageInput) => adminFeedbackService.triage(feedbackId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...feedbackKeys.adminThread, feedbackId] })
      queryClient.invalidateQueries({ queryKey: feedbackKeys.adminQueue })
    },
    onError: (error) => {
      console.error('Error updating feedback item:', error)
      getNotifier().error('Could not update this item. Please try again.')
    },
  })
}

/**
 * Admin reply. `set_status` makes acknowledge and acknowledge-and-close a single
 * request, so the queue and thread are both refreshed on success.
 */
export function useAdminFeedbackReply(feedbackId: string, opts?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ files, ...data }: AdminFeedbackReplyVars) =>
      adminFeedbackService.reply(feedbackId, {
        ...data,
        attachments: await uploadAll(files, feedbackId),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...feedbackKeys.adminThread, feedbackId] })
      queryClient.invalidateQueries({ queryKey: feedbackKeys.adminQueue })
      opts?.onSuccess?.()
    },
    onError: (error) => {
      console.error('Error replying to feedback:', error)
      getNotifier().error('Could not send your reply. Please try again.')
    },
  })
}
